import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { bookNow } from "../../store/Cab/CabAction"
import { cabAction } from "../../store/Cab/CAbReducer"
import { useNavigate } from "react-router-dom"
import Hatchback from "../../assets/cars/hatchback.webp"
import Sedan from "../../assets/cars/sedan.webp"
import SUV from "../../assets/cars/suv.webp"

const CAB_TYPE = {
  1: "Hatchback",
  2: "Sedan",
  3: "SUV",
}



const CAB_TAGLINE = {
  1: "Budget Friendly",
  2: "Most Popular",
  3: "Premium Comfort",
}

const CabCard = ({ showPrice, cab }) => {
  const dispatch = useDispatch()
  const { bookingSuccess, visitorEntryId, web_trip_unique_id, distance } =
    useSelector((state) => state.cab)
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    if (bookingSuccess) {
      dispatch(cabAction.clearBookingSuccess())
      navigate("/bookCab")
    }
  }, [bookingSuccess, dispatch, navigate])

  const submitHandle = (e) => {
    e.stopPropagation()
    const currentTripData = JSON.parse(localStorage.getItem("tripData")) || {}
    const updatedTripData = { ...currentTripData, cab: { ...cab } }
    localStorage.setItem("tripData", JSON.stringify(updatedTripData))

    const data = {
      visitorEntryId,
      web_trip_unique_id,
      vehicle_id: cab?.vehicle_id,
      // The fare API returns this as `total`. Reading `grand_total` here yielded
      // undefined, Sequelize skipped the column, and every booking made through
      // this card was stored with a NULL fare — invisible until someone opened
      // the booking in the admin and found no amount on it.
      grand_total: cab?.total,
      advance_booking: 20,
      advance_booking_amount: 500,
      GST: cab?.gst_percentage,
      GSTCalculate: cab?.gst,
      distance,
      estimate_fare: cab?.subtotal,
    }
    dispatch(bookNow(data))
  }

  const cabNameLower = (cab?.name || "").toLowerCase().replace(/\s+/g, "")
  const carImage = cabNameLower === "hatchback"
    ? Hatchback
    : cabNameLower === "suv"
      ? SUV
      : Sedan

  const cabTypeLabel = CAB_TYPE[cab?.vehicle_id] || "Hatchback"
  const tagline = CAB_TAGLINE[cab?.vehicle_id] || ""
  
  // Model names based on type for "Indica, Swift or similar" feel
  const modelNames = {
    1: "Swift, WagonR or similar",
    2: "Dzire, Etios or similar",
    3: "Innova, Ertiga or similar"
  }[cab?.vehicle_id] || "Swift, WagonR or similar"

  return (
    <div className="cab-card-hz">
      <div className="cab-card-hz__main-row">
        {/* ─── Left: Car visual ─── */}
        <div className="cab-card-hz__visual">
          <img 
            src={carImage} 
            className={`cab-card-hz__car-img cab-card-hz__car-img--${cabNameLower}`} 
            alt={`${cabTypeLabel} cab`} 
          />
        </div>

        {/* ─── Center: Details ─── */}
        <div className="cab-card-hz__details">
          {/* Title block row */}
          <div className="cab-card-hz__title-row">
            {/* Title block */}
            <div className="cab-card-hz__title-block">
              <h3 className="cab-card-hz__title">{cabTypeLabel}</h3>
              <span className="cab-card-hz__subtitle">{modelNames}</span>
            </div>

            {/* Perks badges */}
            <div className="cab-card-hz__perks">
              {tagline && (
                <div className="cab-card-hz__perk-badge cab-card-hz__perk-badge--value">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>
                  <span>{tagline}</span>
                </div>
              )}
              <div className="cab-card-hz__perk-badge cab-card-hz__perk-badge--trust">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                <span>No hidden charges</span>
              </div>
            </div>
          </div>

          {/* Mobile-only toggle */}
          <button 
            className="cab-card-hz__more-toggle" 
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? "View Less" : "View Details"}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={isExpanded ? "rotate-180" : ""}>
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          <div className={`cab-card-hz__expandable ${isExpanded ? 'is-expanded' : ''}`}>
            {/* Divider */}
            <div className="cab-card-hz__divider" />

            {/* Main body content: chips on left, right-stack on right */}
            <div className="cab-card-hz__body-content">
              {/* Feature chips */}
              <div className="cab-card-hz__chips">
                <div className="cab-card-hz__chip">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                  {cab?.seat_capacity} Seats
                </div>
                <div className="cab-card-hz__chip">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/></svg>
                  2 Bags
                </div>
                <div className="cab-card-hz__chip">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/></svg>
                  AC
                </div>
                <div className="cab-card-hz__chip">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  {Math.ceil(distance)} km included
                </div>
              </div>

              {/* Right side stack */}
              <div className="cab-card-hz__right-stack">
                {/* Policies */}
                <div className="cab-card-hz__policies">
                  {cab?.fare_per_km && (
                    <div className="cab-card-hz__policy">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                      <span>Extra km fare: <strong>₹{cab?.fare_per_km}/km</strong> after <strong>{Math.ceil(distance)} km</strong></span>
                    </div>
                  )}
                  <div className="cab-card-hz__policy cab-card-hz__policy--cancel">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    <span>Free cancellation until <strong>4 hours</strong> before</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Right: Price + CTA ─── */}
      <div className="cab-card-hz__action">
        {showPrice && (
          <div className="cab-card-hz__price-block">
            {/* Alternative Design: Inline strikethrough with final price */}
            <div className="cab-card-hz__price-wrapper">
              <span className="cab-card-hz__original-price-inline">
                ₹{parseInt((cab?.total || (cab?.subtotal + cab?.gst)) / 0.9)}
              </span>
              <span className="cab-card-hz__discount-badge-inline">
                10% OFF
              </span>
            </div>
            {/* Final price */}
            <span className="cab-card-hz__price">₹{parseInt(cab?.total || (cab?.subtotal + cab?.gst))}</span>
            <span className="cab-card-hz__price-label">
              Total (incl. ₹{parseInt(cab?.gst)} GST*)
            </span>
          </div>
        )}
        <button className="cab-card-hz__book-btn" onClick={submitHandle}>
          Book {cabTypeLabel}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
          </svg>
        </button>
      </div>
    </div>
  )
}

export default CabCard
